// Carousel posts (docs/plans/2026-10-11-carousel-PLAN.md item 22): one slide at a time with previous/next buttons,
// a "2 / 6" counter, dots and arrow keys. Used only for posts with image_urls (2..10 slides); a single image keeps
// its existing markup at the call site, so agency views are unchanged.
import { useState, type KeyboardEvent } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"

export function hasSlides(post: { imageUrls?: unknown }): post is { imageUrls: string[] } {
  return Array.isArray(post.imageUrls) && post.imageUrls.length > 1
}

interface PostSlidesProps {
  urls: string[]
  alt: string
  /** classes of the frame around the picture (size, aspect ratio, rounding) */
  className?: string
  /** classes of the picture itself */
  imgClassName?: string
  testId?: string
}

export function PostSlides({ urls, alt, className, imgClassName, testId }: PostSlidesProps) {
  const [selected, setIndex] = useState(0)
  const count = urls.length
  // A refetch can shrink the carousel while a later slide is shown: clamp instead of rendering an empty slide.
  const index = Math.min(selected, count - 1)
  const go = (next: number) => setIndex((next + count) % count)
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); go(index - 1) }
    if (e.key === "ArrowRight") { e.preventDefault(); go(index + 1) }
  }
  const stop = (fn: () => void) => (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); fn() }

  return (
    <div
      className={cn("relative group focus:outline-none focus-visible:ring-2 focus-visible:ring-ring", className)}
      role="region"
      aria-roledescription="carousel"
      aria-label={`Karussell mit ${count} Bildern`}
      tabIndex={0}
      onKeyDown={onKey}
      data-testid={testId}
    >
      <img
        src={urls[index]}
        alt={`${alt}, Bild ${index + 1} von ${count}`}
        className={imgClassName}
        loading="lazy"
        aria-live="polite"
        data-testid={testId ? `${testId}-image` : undefined}
      />
      <span
        className="absolute top-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white"
        data-testid={testId ? `${testId}-counter` : undefined}
      >
        {index + 1} / {count}
      </span>
      <button
        type="button"
        onClick={stop(() => go(index - 1))}
        className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-1.5 shadow hover:bg-white"
        aria-label="Vorheriges Bild"
        data-testid={testId ? `${testId}-prev` : undefined}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={stop(() => go(index + 1))}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-1.5 shadow hover:bg-white"
        aria-label="Nächstes Bild"
        data-testid={testId ? `${testId}-next` : undefined}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
        {urls.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={stop(() => setIndex(i))}
            className={cn("h-2 w-2 rounded-full transition-colors", i === index ? "bg-white" : "bg-white/50")}
            aria-label={`Bild ${i + 1} zeigen`}
            aria-current={i === index}
          />
        ))}
      </div>
    </div>
  )
}
